import { supabase } from '../lib/supabase';

export const TRANSACTION_TYPES = {
    PURCHASE: 'purchase',
    SALE: 'sale',
    ADJUSTMENT: 'adjustment',
    RETURN: 'return'
};

// Get stock transactions for a product up to a specific date
export const getStockTransactions = async (productId, upToDate) => {
    try {
        const { data, error } = await supabase
            .from('stock_transactions')
            .select('*')
            .eq('product_id', productId)
            .lte('transaction_date', upToDate)
            .order('transaction_date', { ascending: true })
            .order('created_at', { ascending: true });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching stock transactions:', error);
        return [];
    }
};

export const handleAddStock = async (productId, quantity, notes) => {
    try {
        // Add stock transaction
        await addStockTransaction({
            product_id: productId,
            transaction_type: TRANSACTION_TYPES.PURCHASE, // or ADJUSTMENT
            quantity: parseInt(quantity, 10),
            transaction_date: new Date().toISOString().split('T')[0],
            notes: notes || 'Manual stock addition'
        });

        // Update current stock in products table
        await updateProductStock(productId);

        return true;
    } catch (error) {
        console.error('Error adding stock:', error);
        return false;
    }
};


export const handleEditStock = async (productId, newQuantity, notes) => {
    try {
        // Get current stock
        const currentStock = await getCurrentStock(productId);
        const difference = newQuantity - currentStock;
        
        if (difference !== 0) {
            // Add stock transaction for the difference
            await addStockTransaction({
                product_id: productId,
                transaction_type: TRANSACTION_TYPES.ADJUSTMENT,
                quantity: difference, // Positive for increase, negative for decrease
                transaction_date: new Date().toISOString().split('T')[0],
                notes: notes || `Stock adjusted from ${currentStock} to ${newQuantity} (${difference > 0 ? '+' : ''}${difference})`
            });

            // Update current stock in products table
            await updateProductStock(productId);
        }

        return true;
    } catch (error) {
        console.error('Error editing stock:', error);
        return false;
    }
};

// Calculate stock summary for a product on a specific date
export const calculateStockSummary = async (productId, date) => {
    const transactions = await getStockTransactions(productId, date);

    let openingStock = 0;
    let purchases = 0;
    let sales = 0;

    transactions.forEach(transaction => {
        switch (transaction.transaction_type) {
            case TRANSACTION_TYPES.ADJUSTMENT:
                // Positive adjustments add to stock, negative subtract
                if (transaction.quantity > 0) {
                    openingStock += transaction.quantity;
                } else {
                    sales += Math.abs(transaction.quantity);
                }
                break;
            case TRANSACTION_TYPES.PURCHASE:
                purchases += transaction.quantity;
                break;
            case TRANSACTION_TYPES.SALE:
                sales += Math.abs(transaction.quantity);
                break;
            case TRANSACTION_TYPES.RETURN:
                // Returns add back to stock
                purchases += transaction.quantity;
                break;
            default:
                break;
        }
    });

    sales = Math.max(0, sales);

    const closingStock = openingStock + purchases - sales;

    return {
        openingStock,
        purchases,
        sales,
        closingStock: Math.max(0, closingStock)
    };
};

// Add stock transaction
export const addStockTransaction = async (transaction) => {
    const { data, error } = await supabase
        .from('stock_transactions')
        .insert([transaction])
        .select()
        .single();

    if (error) {
        console.error('Error adding stock transaction:', error);
        throw error;
    }

    return data;
};

// Update current stock in products table - includes all transactions (incl. future-dated)
export const updateProductStock = async (productId) => {
    try {
        const farFuture = '9999-12-31';
        const stockSummary = await calculateStockSummary(productId, farFuture);

        const { error } = await supabase
            .from('products')
            .update({ current_stock: stockSummary.closingStock })
            .eq('id', productId);

        if (error) {
            console.error('Error updating product stock:', error);
            throw error;
        }
        return stockSummary.closingStock;
    } catch (error) {
        console.error('Error updating product stock:', error);
        throw error;
    }
};

// Get current stock for a product
export const getCurrentStock = async (productId) => {
    try {
        const { data, error } = await supabase
            .from('products')
            .select('current_stock')
            .eq('id', productId)
            .maybeSingle();

        if (error) throw error;
        return data?.current_stock || 0;
    } catch (error) {
        console.error('Error getting current stock:', error);
        return 0;
    }
};
